import { CheckIcon } from '@heroicons/react/16/solid';
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  CircleStackIcon,
  CodeBracketIcon,
  DocumentDuplicateIcon,
  LightBulbIcon,
  MegaphoneIcon
} from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import Modal from 'components/common/modal';
import Button from 'components/inputs/button';
import ToggleSwitch from 'components/inputs/toggle-switch';
import CompatibleCombinationsBrowserModal from 'components/layout/modals/compatible-combinations-browser-modal';
import ExportStructureModal from 'components/layout/modals/export-structure-modal';
import ReportIssueModal from 'components/layout/modals/report-issue-modal';
import { ExposureDirectionType } from 'data/types';
import { setActiveConfig, setConfigurations } from 'features/configurations';
import {
  setAddressId,
  setAddressSearched,
  setAttomData,
  setLocationData,
  setProjectSettings,
  setPropertyLocation,
  setWeatherData,
  updateProjectSetting
} from 'features/project';
import {
  setCondenserLocations,
  setConditionedLevels,
  setExistingSystems,
  setFootprint,
  setNotes,
  setPartialStructure,
  setStructure,
  setStructureSubtypeId,
  setStructureTypeId
} from 'features/structure';
import {
  resetTips,
  setActiveLevelIndex,
  setPendingImportCompletedSteps,
  setShowDebugMenu,
  toggleDeveloperMode
} from 'features/ui';
import useHasSystemsQueryRun from 'hooks/use-has-systems-query-run';
import useSqlCaptureEnabled from 'hooks/use-sql-capture-enabled';
import { nanoid } from 'nanoid';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { restoreConfigurationsFromExport } from 'utils/configuration-restore';
import { updateConfigZoneItems } from 'utils/configurations';
import { restoreProjectSettings } from 'utils/data';
import { buildDebugExport } from 'utils/debug-export';
import { reportError } from 'utils/error-log/error-log-reporter';
import { clearAnalyzingFlags } from 'utils/existing-equipment';
import { fetchLastSystemsQuerySql } from 'utils/last-systems-query-sql';
import { sqlCaptureSetting } from 'utils/sql-capture-setting';
import { getStructureImportCompletionSteps } from 'utils/structure-import-completion';
import {
  buildExportedProjectJson,
  downloadStructureLevels,
  parseAndExpandStructureLevelsJson
} from 'utils/structure-json';
import { systemsQuerySqlCopyTooltip } from 'utils/systems-query-sql-copy-tooltip';
import { saveDismissedTips, saveTipsSkipped } from 'utils/tips';
import { errorModal, successModal } from 'utils/ui';

export default function DebugMenu() {
  const dispatch = useAppDispatch();
  const projectStateFull = useAppSelector((state) => state.project);
  const configState = useAppSelector((state) => state.configurations);
  const structureState = useAppSelector((state) => state.structure);
  const systemState = useAppSelector((state) => state.systems);
  const { showDebugMenu, developerMode, ...uiState } = useAppSelector(
    (state) => state.ui
  );
  const dataState = useAppSelector((state) => state.data);
  const [open, setOpen] = useState(false);
  const [variablesLogged, setVariablesLogged] = useState(false);
  const [stateCopied, setStateCopied] = useState(false);
  const [sqlCopied, setSqlCopied] = useState(false);
  const [exportNameModalOpen, setExportNameModalOpen] = useState(false);
  const [exportDefaultName, setExportDefaultName] = useState('');
  const [reportIssueModalOpen, setReportIssueModalOpen] = useState(false);
  // Default ON: the browser modal should open showing active equipment only
  // (its own fallback default matches; this toggle is the source of truth
  // for the fetchParams it is opened with).
  const [activeOnly, setActiveOnly] = useState(true);
  // Default ON: same source-of-truth arrangement for the in-stock filter —
  // the fetchParams value wins over the modal's own fallback default.
  const [inStockOnly, setInStockOnly] = useState(true);
  const [matchingMode, setMatchingMode] = useState<'general' | 'exact'>(
    'general'
  );
  const [browserModalOpen, setBrowserModalOpen] = useState(false);
  const [browserFetchParams, setBrowserFetchParams] = useState<{
    systemUniqueId: string;
    activeOnly: boolean;
    inStockOnly: boolean;
    matchingMode: 'general' | 'exact';
  } | null>(null);
  const existingSystems = useAppSelector(
    (state) => state.structure.existingSystems
  );
  const hasSystemsQueryRun = useHasSystemsQueryRun();
  const sqlCaptureEnabled = useSqlCaptureEnabled();
  const structureJsonInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setOpen(showDebugMenu);
    setVariablesLogged(false);
    setStateCopied(false);
    setSqlCopied(false);
  }, [showDebugMenu]);

  const structureOrientation = useMemo(
    () =>
      projectStateFull.projectSettings.find(
        (s) => s.code === 'structure_orientation'
      )?.value as ExposureDirectionType | undefined,
    [projectStateFull.projectSettings]
  );

  const logVariables = () => {
    console.groupCollapsed('Variable Dump');
    console.log('project:', projectStateFull);
    console.log('configurations:', configState);
    console.log('structure:', structureState);
    console.log('systems:', systemState);
    console.log('ui:', uiState);
    console.log('data:', dataState);
    console.groupEnd();

    setVariablesLogged(true);
  };

  const copyStateToClipboard = async () => {
    const payload = buildDebugExport(
      projectStateFull,
      structureState,
      configState
    );
    await navigator.clipboard.writeText(JSON.stringify(payload));
    setStateCopied(true);
  };

  const copySystemsQuerySql = async () => {
    try {
      const sql = await fetchLastSystemsQuerySql();
      await navigator.clipboard.writeText(sql);
      setSqlCopied(true);
    } catch (error) {
      errorModal(
        error instanceof Error
          ? error.message
          : 'Failed to copy the systems query SQL'
      );
    }
  };

  const handleStructureExportClick = () => {
    setExportDefaultName(`structure-${nanoid(5)}`);
    dispatch(setShowDebugMenu(false));
    setExportNameModalOpen(true);
  };

  const handleExportConfirm = (name: string) => {
    downloadStructureLevels(
      {
        levels: structureState.structure,
        orientation: structureOrientation,
        footprint: structureState.footprint,
        condenserLocations: structureState.condenserLocations,
        project: buildExportedProjectJson(projectStateFull, structureState),
        configurations: configState.configurations
      },
      name
    );
    successModal('Structure JSON exported');
  };

  const handleStructureImportButtonClick = () => {
    structureJsonInputRef.current?.click();
  };

  const handleReportIssueClick = () => {
    dispatch(setShowDebugMenu(false));
    setReportIssueModalOpen(true);
  };

  const handleReportIssueConfirm = async (
    subject: string,
    explanation: string
  ) => {
    const sent = await reportError({
      source: 'manualReport',
      message: subject,
      explanation
    });
    if (sent) {
      successModal('Issue reported. Thank you!');
      return;
    }
    errorModal('Failed to send the issue report. Please try again.');
  };

  const handleResetTips = () => {
    saveDismissedTips([]);
    saveTipsSkipped(false);
    dispatch(resetTips());
    dispatch(setShowDebugMenu(false));
  };

  const handleStructureImportFileChange = async (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      const fileContent = await file.text();
      const {
        orientation,
        footprint,
        condenserLocations,
        levels,
        project,
        configurations
      } = parseAndExpandStructureLevelsJson(fileContent);
      if (!project && orientation) {
        dispatch(
          updateProjectSetting({
            attributeCode: 'structure_orientation',
            value: orientation
          })
        );
      }
      if (footprint && footprint.length > 0) {
        dispatch(setFootprint(footprint));
      }
      dispatch(setCondenserLocations(condenserLocations));
      dispatch(setStructure(levels));
      dispatch(setActiveLevelIndex(0));
      if (project) {
        if (project.projectSettings.length > 0) {
          dispatch(
            setProjectSettings(restoreProjectSettings(project.projectSettings))
          );
        }
        dispatch(setAttomData(project.attomData));
        dispatch(setLocationData(project.locationData));
        dispatch(setPropertyLocation(project.propertyLocation));
        dispatch(setWeatherData(project.weatherData));
        dispatch(setAddressId(project.addressId));
        dispatch(
          setAddressSearched(Boolean(project.attomData || project.locationData))
        );
        dispatch(setStructureTypeId(project.structureTypeId));
        dispatch(setStructureSubtypeId(project.structureSubtypeId));
        dispatch(setPartialStructure(project.partialStructure));
        dispatch(setConditionedLevels(project.conditionedLevels));
        dispatch(
          setExistingSystems(clearAnalyzingFlags(project.existingSystems))
        );
        dispatch(setNotes(project.notes));
      }
      dispatch(
        setConfigurations(
          configurations && configurations.length > 0
            ? updateConfigZoneItems(
                restoreConfigurationsFromExport(configurations, levels)
              )
            : []
        )
      );
      dispatch(setActiveConfig(0));
      dispatch(
        setPendingImportCompletedSteps(getStructureImportCompletionSteps())
      );
      successModal('Structure JSON imported');
      dispatch(setShowDebugMenu(false));
    } catch (error) {
      if (error instanceof Error) {
        errorModal(error.message);
      } else {
        errorModal('Failed to import structure JSON');
      }
    } finally {
      if (structureJsonInputRef.current) {
        structureJsonInputRef.current.value = '';
      }
    }
  };

  return (
    <>
      <Modal
        title='Debug Menu'
        openState={[open, setOpen]}
        onClose={() => dispatch(setShowDebugMenu(false))}
        infoMode
        ignoreInErrorLogScreenshot
      >
        <div className='flex flex-wrap items-center justify-center gap-3'>
          <Button
            text={variablesLogged ? 'Variables Logged' : 'Log Variables'}
            onClick={logVariables}
            disabled={variablesLogged}
            icon={variablesLogged ? CheckIcon : CodeBracketIcon}
          />
          <Button
            text={
              stateCopied ? 'Project Context Copied!' : 'Copy Project Context'
            }
            onClick={copyStateToClipboard}
            disabled={stateCopied}
            icon={stateCopied ? CheckIcon : DocumentDuplicateIcon}
          />
          <Button
            text='Export Structure JSON'
            onClick={handleStructureExportClick}
            icon={ArrowUpTrayIcon}
          />
          <Button
            text='Import Structure JSON'
            onClick={handleStructureImportButtonClick}
            icon={ArrowDownTrayIcon}
          />
          <Button
            text={
              sqlCopied ? 'Systems Query SQL Copied!' : 'Copy Systems Query SQL'
            }
            onClick={copySystemsQuerySql}
            disabled={sqlCopied || !hasSystemsQueryRun}
            tooltip={systemsQuerySqlCopyTooltip(
              hasSystemsQueryRun,
              sqlCaptureEnabled
            )}
            icon={sqlCopied ? CheckIcon : CircleStackIcon}
          />
          <Button
            text='Reset Tips'
            onClick={handleResetTips}
            icon={LightBulbIcon}
          />
          <Button
            text='Report Issue'
            onClick={handleReportIssueClick}
            icon={MegaphoneIcon}
          />
          <input
            ref={structureJsonInputRef}
            type='file'
            accept='application/json,.json'
            className='hidden'
            onChange={handleStructureImportFileChange}
          />
        </div>
        <div className='mt-4 flex flex-col items-center gap-3 border-t border-gray-200 pt-4'>
          <div className='flex items-center gap-3'>
            <Button
              text='Get Compatible Combinations'
              onClick={() => {
                const activeSystem = existingSystems[0];
                if (!activeSystem) return;
                setBrowserFetchParams({
                  systemUniqueId: activeSystem.uniqueId,
                  activeOnly,
                  inStockOnly,
                  matchingMode
                });
                setBrowserModalOpen(true);
                dispatch(setShowDebugMenu(false));
              }}
              disabled={existingSystems.length === 0}
            />
            <label className='flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-700'>
              <ToggleSwitch
                value={activeOnly}
                onChange={() => setActiveOnly((prev) => !prev)}
              />
              {activeOnly ? 'Active Only' : 'All Equipment'}
            </label>
            <label className='flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-700'>
              <ToggleSwitch
                value={inStockOnly}
                onChange={() => setInStockOnly((prev) => !prev)}
              />
              {inStockOnly ? 'In Stock Only' : 'All Stock'}
            </label>
            <label className='flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-700'>
              <ToggleSwitch
                value={matchingMode === 'exact'}
                onChange={() =>
                  setMatchingMode((prev) =>
                    prev === 'general' ? 'exact' : 'general'
                  )
                }
              />
              {matchingMode === 'general' ? 'General' : 'Exact'}
            </label>
          </div>
        </div>
        <div className='mt-4 flex flex-wrap items-center justify-center gap-6 border-t border-gray-200 pt-4'>
          <label className='flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-700'>
            <ToggleSwitch
              value={developerMode}
              onChange={() => dispatch(toggleDeveloperMode())}
            />
            Developer Mode
          </label>
          <label className='flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-700'>
            <ToggleSwitch
              value={sqlCaptureEnabled}
              onChange={(enabled) => sqlCaptureSetting.setEnabled(enabled)}
            />
            Capture Systems Query SQL
          </label>
        </div>
      </Modal>
      <ExportStructureModal
        openState={[exportNameModalOpen, setExportNameModalOpen]}
        defaultName={exportDefaultName}
        onConfirm={handleExportConfirm}
      />
      <ReportIssueModal
        openState={[reportIssueModalOpen, setReportIssueModalOpen]}
        onConfirm={handleReportIssueConfirm}
      />
      <CompatibleCombinationsBrowserModal
        openState={[browserModalOpen, setBrowserModalOpen]}
        fetchParams={browserFetchParams}
      />
    </>
  );
}
